import React, { useEffect, useRef } from 'react';
import { Verified as VerifiedIcon } from '@material-ui/icons';
import Card from '../../../common/card/Card';
import CardContent from '../../../common/card/CardContent';
import CardTitle from '../../../common/card/CardTitle';
import InlineForm from '../../../common/inline/InlineForm';
import InlineFormik from '../../../common/inline/InlineFormik';
import { useUpdateChangeOrder } from '../ChangeOrder.hook';
import {
  BaseChangeOrderCardProps,
  ChangeOrder
} from '../ChangeOrder.interface';
import InlineHtml from '../../../common/inline/InlineHtml';
import { useUpdateTask } from '../../tasks/Task.hooks';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import InlineDatePicker from '../../../common/inline/InlineDatePicker';

export interface ChangeOrderVerificationCardProps
  extends BaseChangeOrderCardProps {
  changeOrder: ChangeOrder;
  setSuccessMessage: (message: string) => void;
  setErrorMessage: (message: string) => void;
}

export const ChangeOrderVerificationCard: React.FC<
  ChangeOrderVerificationCardProps
> = props => {
  const { changeOrder, onChange, setSuccessMessage, setErrorMessage } = props;
  const onChangeRef = useRef(onChange);
  const {
    updatedTask,
    updateError: updateTaskError,
    updateTask
  } = useUpdateTask(changeOrder.task.id || 0);
  const { updatedChangeOrder, isLoading, error, updateChangeOrder } =
    useUpdateChangeOrder(changeOrder.id);
  const { isFieldDisabled } = useTaskEntityFields(changeOrder.task);

  const initialValues: Partial<ChangeOrder> = {
    verificationNotes: changeOrder.verificationNotes || '',
    verificationDueDate: changeOrder.verificationDueDate,
    verificationCompletedDate: changeOrder.verificationCompletedDate
  };
  useEffect(() => {
    if (updatedChangeOrder?.id) {
      setSuccessMessage('Change Order successfully updated');
      onChangeRef.current?.(updatedChangeOrder);
    }
  }, [setSuccessMessage, updatedChangeOrder]);

  useEffect(() => {
    if (updatedTask) {
      changeOrder.task = Object.assign(changeOrder.task || {}, updatedTask);
      onChange?.(changeOrder);
    }
  }, [updatedTask]);

  useEffect(() => {
    if (Boolean(error || updateTaskError))
      setErrorMessage('There was an error saving');
  }, [setErrorMessage, error, updateTaskError]);

  return (
    <InlineFormik
      initialValues={initialValues}
      strict={false}
      isSubmitting={isLoading}
      error={error}
      onSubmit={(updatedValue: Partial<ChangeOrder>) => {
        const dto = { ...updatedValue };
        updateChangeOrder(dto);
      }}
    >
      <Card>
        <CardTitle>
          <VerifiedIcon />
          Verification
        </CardTitle>
        <CardContent padding='large'>
          <InlineForm>
            <InlineHtml
              name='verificationNotes'
              label='Verification Notes'
              disabled={isFieldDisabled('verificationNotes')}
              helperText='Provide any additional notes here'
              attachments={changeOrder.task.attachments || []}
              onChangeAttachments={attachments => {
                updateTask({ attachments });
                if (changeOrder.task) {
                  changeOrder.task.attachments = attachments;
                }
                onChange?.(changeOrder);
              }}
            />
            <InlineDatePicker
              grid={{ lg: 6, md: 6 }}
              clearable
              name='verificationDueDate'
              label='Due Date'
              placeholder='Enter Due Date'
              disabled={isFieldDisabled('verificationDueDate')}
              autoCloseOnSelect
            />
            <InlineDatePicker
              grid={{ lg: 6, md: 6 }}
              clearable
              name='verificationCompletedDate'
              label='Completed Date'
              placeholder='Enter Completed Date'
              disabled={isFieldDisabled('verificationCompletedDate')}
              autoCloseOnSelect
            />
          </InlineForm>
        </CardContent>
      </Card>
    </InlineFormik>
  );
};
